import { useEffect, useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { gsap } from 'gsap'
import { Carousel } from 'react-responsive-carousel'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'
import pnlProject from '../../content/blogs/pnl/registry'
import projects from '../../data/projects'
import OnDemandVideo from '../../components/OnDemandVideo/OnDemandVideo'
import ImageLightbox from '../../components/ImageLightbox/ImageLightbox'
import { logProjectOpen } from '../../supabaseTracker'
import './ProjectDetail.css'

function parseFrontmatter(text) {
  const match = text.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/)
  if (!match) return { attributes: {}, body: text }

  const yamlBlock = match[1]
  const body = match[2]
  const attributes = {}

  yamlBlock.split('\n').forEach((line) => {
    const colonIdx = line.indexOf(':')
    if (colonIdx === -1) return
    const key = line.slice(0, colonIdx).trim()
    let val = line.slice(colonIdx + 1).trim()

    if (
      (val.startsWith('"') && val.endsWith('"')) ||
      (val.startsWith("'") && val.endsWith("'"))
    ) {
      val = val.slice(1, -1)
    }
    if (val.startsWith('[') && val.endsWith(']')) {
      try {
        val = JSON.parse(val)
      } catch (e) {
        // keep as string
      }
    }
    attributes[key] = val
  })

  return { attributes, body }
}

const ProjectDetail = () => {
  const { slug } = useParams()
  const [content, setContent] = useState('')
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [markdownVisible, setMarkdownVisible] = useState(false)
  const [selectedScreenshot, setSelectedScreenshot] = useState(0)
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [readProgress, setReadProgress] = useState(0)
  const headerRef = useRef(null)
  const mediaRef = useRef(null)
  const contentRef = useRef(null)
  const progressRef = useRef(null)

  const projectEntry =
    pnlProject.slug === slug ? pnlProject : projects.find((p) => p.slug === slug) || null
  const isPnl = Boolean(projectEntry && 'markdownUrl' in projectEntry)

  useEffect(() => {
    window.scrollTo(0, 0)
    setMarkdownVisible(false)
    setSelectedScreenshot(0)
  }, [slug])

  useEffect(() => {
    if (slug) logProjectOpen(slug)
  }, [slug])

  useEffect(() => {
    if (!isPnl || loading || markdownVisible || !contentRef.current) return undefined
    if (!('IntersectionObserver' in window)) {
      setMarkdownVisible(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMarkdownVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '600px 0px' }
    )
    observer.observe(contentRef.current)
    return () => observer.disconnect()
  }, [isPnl, loading, markdownVisible, slug])

  useEffect(() => {
    // Only P&L has markdown right now; other projects show a basic detail page
    if (!projectEntry || !('markdownUrl' in projectEntry)) {
      setLoading(false)
      return
    }

    fetch(projectEntry.markdownUrl)
      .then((res) => res.text())
      .then((text) => {
        const { attributes, body } = parseFrontmatter(text)
        setMeta(attributes)
        setContent(body)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [slug, projectEntry])

  // Entry animations after content loads
  useEffect(() => {
    if (loading || !projectEntry) return

    const tl = gsap.timeline()
    if (headerRef.current) {
      tl.fromTo(
        headerRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }
      )
    }
    if (mediaRef.current) {
      tl.fromTo(
        mediaRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' },
        '-=0.4'
      )
    }
    if (contentRef.current) {
      tl.fromTo(
        contentRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' },
        '-=0.3'
      )
    }
  }, [loading, meta, projectEntry])

  // Reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0
      setReadProgress(Math.min(progress, 100))
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (loading) {
    return (
      <div className="pd-loading">
        <div className="pd-spinner" />
        <p className="pd-loading-text">Loading project...</p>
      </div>
    )
  }

  if (!projectEntry) {
    return (
      <div className="pd-not-found">
        <h1>Project not found</h1>
        <Link to="/projects" className="pd-back-link">
          ← Back to Projects
        </Link>
      </div>
    )
  }

  const resolveImage = (src) => {
    if (isPnl && projectEntry.images && projectEntry.images[src]) {
      return projectEntry.images[src]
    }
    return src
  }

  const resolveVideo = (src) => {
    if (isPnl && projectEntry.videos && projectEntry.videos[src]) {
      return projectEntry.videos[src]
    }
    return src
  }

  const projectGallery = !isPnl && projectEntry.featuredImage
    ? [projectEntry.featuredImage, ...(projectEntry.images || [])]
    : projectEntry.images || []
  const lightboxImages = isPnl
    ? (projectEntry.screenshots || []).map((shot) => shot.src)
    : projectGallery

  return (
    <>
      {/* Reading progress bar */}
      <div
        className="pd-progress-bar"
        ref={progressRef}
        style={{ width: `${readProgress}%` }}
      />

      <article className="pd">
        {/* Header */}
        <header className="pd-header" ref={headerRef}>
          <Link to="/projects" className="pd-back-link">
            ← Back to Projects
          </Link>

          {isPnl && (
            <div className="pd-tags">
              {projectEntry.tags &&
                projectEntry.tags.map((tag) => (
                  <span key={tag} className="pd-tag">
                    {tag}
                  </span>
                ))}
            </div>
          )}

          <h1 className="pd-title">{projectEntry.title}</h1>
          {isPnl && <p className="pd-subtitle">{projectEntry.subtitle}</p>}

          {isPnl && (
            <div className="pd-meta">
              <span className="pd-meta__author">{projectEntry.author}</span>
              <span className="pd-meta__dot">·</span>
              <span>
                {new Date(projectEntry.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
              {projectEntry.readTime && (
                <>
                  <span className="pd-meta__dot">·</span>
                  <span>{projectEntry.readTime}</span>
                </>
              )}
            </div>
          )}

          <div className="pd-app-links">
            {isPnl && projectEntry.appStoreLink && (
              <a
                href={projectEntry.appStoreLink}
                target="_blank"
                rel="noopener noreferrer"
                className="pd-app-link"
              >
                <svg className="pd-app-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-.8 1.94-.8s.16 1.06-.59 1.91c-.69.81-1.85.78-1.85.78s-.17-1.06 .5-1.89" />
                </svg>
                <span>App Store</span>
              </a>
            )}
            {isPnl && projectEntry.playStoreLink && (
              <a
                href={projectEntry.playStoreLink}
                target="_blank"
                rel="noopener noreferrer"
                className="pd-app-link"
              >
                <svg className="pd-app-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5v-17c0-0.83,0.94-1.3,1.6-0.8l14,8.5c0.6,0.37,0.6,1.23,0,1.6l-14,8.5C3.94,21.8,3,21.33,3,20.5z" />
                </svg>
                <span>Play Store</span>
              </a>
            )}
            {projectEntry.liveLink && (
              <a
                href={projectEntry.liveLink}
                target="_blank"
                rel="noopener noreferrer"
                className="pd-app-link"
              >
                <svg className="pd-app-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                <span>Live Website</span>
              </a>
            )}
            {projectEntry.demoLink && (
              <a
                href={projectEntry.demoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="pd-app-link"
              >
                <span>Demo Video</span>
              </a>
            )}
            {projectEntry.repoLink && (
              <a href={projectEntry.repoLink} target="_blank" rel="noopener noreferrer" className="pd-app-link">
                <span>GitHub Repository</span>
              </a>
            )}
            {projectEntry.reportLink && (
              <a href={projectEntry.reportLink} target="_blank" rel="noopener noreferrer" className="pd-app-link">
                <span>GSoC Final Report</span>
              </a>
            )}
            {projectEntry.gsocLink && (
              <a href={projectEntry.gsocLink} target="_blank" rel="noopener noreferrer" className="pd-app-link">
                <span>Google Summer of Code</span>
              </a>
            )}
          </div>
        </header>

        {/* Media Section — video + screenshot carousel */}
        <div className="pd-media" ref={mediaRef}>
          {isPnl && projectEntry.demoVideo && (
            <div className="pd-media__video-wrap">
              <div className="pd-media__section-label">Demo Video</div>
              <OnDemandVideo
                src={projectEntry.demoVideo}
                poster={projectEntry.coverLight}
                title="P&L App demo video"
              />
            </div>
          )}

          {((isPnl && projectEntry.screenshots && projectEntry.screenshots.length > 0) ||
            (!isPnl && (projectGallery.length > 0 || projectEntry.demoLink))) && (
            <div className="pd-media__carousel-wrap">
              <div className="pd-media__section-label">Project Gallery</div>
              <Carousel
                showArrows={true}
                showThumbs={false}
                showStatus={true}
                infiniteLoop={true}
                autoPlay={false}
                swipeable={true}
                emulateTouch={true}
                useKeyboardArrows={true}
                selectedItem={selectedScreenshot}
                onChange={setSelectedScreenshot}
              >
                {isPnl
                  ? projectEntry.screenshots.map((shot, idx) => (
                      <div key={idx} className="pd-media__slide">
                        {Math.min(
                          (idx - selectedScreenshot + projectEntry.screenshots.length) % projectEntry.screenshots.length,
                          (selectedScreenshot - idx + projectEntry.screenshots.length) % projectEntry.screenshots.length
                        ) <= 1 && (
                          <button
                            type="button"
                            className="pd-media__image-trigger"
                            onClick={() => setLightboxIndex(idx)}
                            aria-label={`View ${shot.caption || 'project image'} full screen`}
                          >
                            <img
                              src={shot.src}
                              alt={shot.caption}
                              className="pd-media__slide-img"
                              loading={idx === selectedScreenshot ? 'eager' : 'lazy'}
                            />
                          </button>
                        )}
                        {shot.caption && <p className="legend">{shot.caption}</p>}
                      </div>
                    ))
                  : projectGallery.map((src, idx) => (
                      <div key={idx} className="pd-media__slide">
                        <button
                          type="button"
                          className="pd-media__image-trigger"
                          onClick={() => setLightboxIndex(idx)}
                          aria-label={`View ${projectEntry.title} image ${idx + 1} full screen`}
                        >
                          <img
                            src={src}
                            alt={projectEntry.featuredImage && idx === 0
                              ? `${projectEntry.title} cover`
                              : `${projectEntry.title} screenshot ${idx + 1}`}
                            className="pd-media__slide-img"
                            loading="lazy"
                          />
                        </button>
                      </div>
                    )).concat(projectEntry.demoLink ? [
                      <div key={`${projectEntry.slug}-demo-video`} className="pd-media__slide pd-media__slide--video">
                        <iframe
                          src="https://www.youtube-nocookie.com/embed/MwNOuVCunjQ?rel=0"
                          title={`${projectEntry.title} demo video`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                          loading="lazy"
                        />
                      </div>,
                    ] : [])}
              </Carousel>
            </div>
          )}
        </div>

        {/* Markdown content */}
        <div className="pd-content" ref={contentRef}>
          {!isPnl && (
            <div className="pd-basic">
              <h2 className="pd-h2">Overview</h2>
              <p className="pd-p">{projectEntry.description}</p>

              {projectEntry.caseStudy ? (
                projectEntry.caseStudy.map((section) => (
                  <section key={section.title}>
                    <h3 className="pd-h3">{section.title}</h3>
                    <p className="pd-p">{section.body}</p>
                  </section>
                ))
              ) : (
                <>
                  <h3 className="pd-h3">What I delivered</h3>
                  {projectEntry.highlights ? (
                <ul className="pd-ul">
                  {projectEntry.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
                </ul>
                  ) : (
              <ul className="pd-ul">
                <li>Responsive UI, clean UX, and production-ready deployment</li>
                <li>Performance and SEO-focused page structure</li>
                <li>Reliable integrations (auth, payments, CMS, maps, chat) depending on project needs</li>
              </ul>
                  )}
                </>
              )}

              <hr className="pd-hr" />
            </div>
          )}

          {isPnl ? (
          markdownVisible ? <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              img: ({ src, alt }) => (
                <figure className="pd-figure">
                  <img
                    src={resolveImage(src)}
                    alt={alt}
                    className="pd-image"
                    loading="lazy"
                  />
                  {alt && <figcaption className="pd-figcaption">{alt}</figcaption>}
                </figure>
              ),

              video: ({ src, alt }) => (
                <figure className="pd-figure">
                  <video
                    src={resolveVideo(src)}
                    className="pd-video"
                    controls
                    playsInline
                  />
                  {alt && <figcaption className="pd-figcaption">{alt}</figcaption>}
                </figure>
              ),

              code: ({ node, inline, className, children, ...props }) => {
                const match = /language-(\w+)/.exec(className || '')
                if (!inline && match) {
                  return (
                    <div className="pd-code-block">
                      <div className="pd-code-header">
                        <span className="pd-code-lang">{match[1]}</span>
                      </div>
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        customStyle={{
                          margin: 0,
                          borderRadius: '0 0 12px 12px',
                          fontSize: '0.87rem',
                          padding: '1.5em',
                        }}
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    </div>
                  )
                }
                return (
                  <code className="pd-inline-code" {...props}>
                    {children}
                  </code>
                )
              },

              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pd-link"
                >
                  {children}
                </a>
              ),

              blockquote: ({ children }) => (
                <blockquote className="pd-blockquote">{children}</blockquote>
              ),

              ul: ({ children }) => <ul className="pd-ul">{children}</ul>,
              ol: ({ children }) => <ol className="pd-ol">{children}</ol>,

              h2: ({ children }) => {
                const id = String(children)
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/(^-|-$)/g, '')
                return (
                  <h2 id={id} className="pd-h2">
                    {children}
                  </h2>
                )
              },
              h3: ({ children }) => {
                const id = String(children)
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/(^-|-$)/g, '')
                return (
                  <h3 id={id} className="pd-h3">
                    {children}
                  </h3>
                )
              },
              h4: ({ children }) => <h4 className="pd-h4">{children}</h4>,

              p: ({ children }) => <p className="pd-p">{children}</p>,
              hr: () => <hr className="pd-hr" />,

              strong: ({ children }) => (
                <strong className="pd-strong">{children}</strong>
              ),

              table: ({ children }) => (
                <div className="pd-table-wrap">
                  <table className="pd-table">{children}</table>
                </div>
              ),
              thead: ({ children }) => (
                <thead className="pd-thead">{children}</thead>
              ),
              th: ({ children }) => <th className="pd-th">{children}</th>,
              td: ({ children }) => <td className="pd-td">{children}</td>,
            }}
          >
            {content}
          </ReactMarkdown> : <div className="pd-content__placeholder" aria-hidden="true" />
          ) : null}
        </div>

        <footer className="pd-footer">
          <Link to="/projects" className="pd-back-link">
            ← Back to Projects
          </Link>
        </footer>
      </article>
      {lightboxIndex !== null && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          title={projectEntry.title}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  )
}

export default ProjectDetail
